import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import mergeRefs from 'react-merge-refs';
import { InputContainer, sharedInputStyles, textFieldSizes } from "../TextField/TextField.styles";
import { motion, useMotionValue } from 'framer-motion';
import { useIsomorphicLayoutEffect } from "../../hooks";
import React, { useRef } from 'react';
import styled from 'styled-components';
import { ComboBoxContext } from "./ComboBoxContext";
import { IconChevronDown } from "@frameio/vapor-icons/build/esm/icons/IconChevronDown";
import { InputAdornment } from "../TextField/InputAdornment";
import { theme } from "../../theme";
export var ComboBoxInputSelect = React.forwardRef(function (_ref, forwardedRef) {
  var _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'medium' : _ref$size,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$placeholder = _ref.placeholder,
      placeholder = _ref$placeholder === void 0 ? '' : _ref$placeholder,
      itemToString = _ref.itemToString; // Refs

  var shadowRef = useRef(null);
  var inputContainerRef = useRef(null);
  var localInputRef = useRef(null); // Dynamic input width

  var inputWidth = useMotionValue(0); // Combobox specific context

  var context = React.useContext(ComboBoxContext);
  var getInputProps = context.getInputProps,
      selectedItem = context.selectedItem,
      inputRef = context.inputRef,
      query = context.query,
      openMenu = context.openMenu;

  var _getInputProps = getInputProps(),
      ref = _getInputProps.ref,
      inputProps = _objectWithoutProperties(_getInputProps, ["ref"]); // Only show the placeholder when there's no query or selected item


  var showingPlaceholder = placeholder && !query && !selectedItem;
  /**
   * We measure the text content to update the width of our input
   * field as the user types. The max width is the entire width
   * of the container.
   */

  function updateInputWidth(val) {
    var maxWidth = inputContainerRef.current.getBoundingClientRect().width;

    if (showingPlaceholder) {
      shadowRef.current.textContent = placeholder;
    } else {
      shadowRef.current.textContent = val;
    }

    var measuredValueInputWidth = shadowRef.current.scrollWidth + 2;

    if (measuredValueInputWidth > maxWidth) {
      return;
    }

    inputWidth.set(measuredValueInputWidth);
  }
  /**
   * Whenever our component is about to rerender, let's
   * measure and update our input width.
   */


  useIsomorphicLayoutEffect(function () {
    updateInputWidth(query);
  });
  /**
   * Focus the input and open the menu upon mousedown, similar to
   * a native Select menu.
   */

  function onMouseDown(e) {
    // eslint-disable-next-line react-hooks-ssr/react-hooks-global-ssr
    if (document.activeElement !== localInputRef.current) {
      e.preventDefault();
      localInputRef.current.focus();
    }

    openMenu();
  }

  var selectedItemText = !query && selectedItem && itemToString(selectedItem);
  return /*#__PURE__*/React.createElement(StyledInputContainer // It should be impossible for this input to attain error status
  , {
    $hasError: false,
    disabled: disabled,
    onMouseDown: onMouseDown,
    ref: mergeRefs([inputContainerRef, inputRef])
  }, /*#__PURE__*/React.createElement(StyledValueContainer, {
    tabIndex: -1,
    ref: forwardedRef
  }, /*#__PURE__*/React.createElement(Input, _extends({
    ref: mergeRefs([localInputRef, ref]),
    style: {
      width: inputWidth
    },
    value: query,
    onFocus: openMenu,
    placeholder: showingPlaceholder ? placeholder : undefined,
    $size: size
  }, inputProps)), /*#__PURE__*/React.createElement(InputShadow, {
    $size: size,
    "aria-hidden": true,
    ref: shadowRef
  }), /*#__PURE__*/React.createElement(InputAdornment, {
    $size: size,
    $placement: "end"
  }, /*#__PURE__*/React.createElement(IconChevronDown, {
    size: size === 'medium' ? 24 : 16
  }))), selectedItemText && /*#__PURE__*/React.createElement(ItemText, {
    $size: size
  }, selectedItemText));
});
var StyledInputContainer = styled(InputContainer).withConfig({
  displayName: "ComboBoxInputSelect__StyledInputContainer",
  componentId: "vapor__sc-1ggv4n5-0"
})(["display:block;position:relative;"]);
var ItemText = styled.div.withConfig({
  displayName: "ComboBoxInputSelect__ItemText",
  componentId: "vapor__sc-1ggv4n5-1"
})(["position:absolute;top:0;left:0;", ";", ";pointer-events:none;white-space:nowrap;text-overflow:ellipsis;overflow:hidden;padding-right:", ";"], sharedInputStyles, function (p) {
  return textFieldSizes[p.$size];
}, theme.spaces[32]);
var Input = styled(motion.input).withConfig({
  displayName: "ComboBoxInputSelect__Input",
  componentId: "vapor__sc-1ggv4n5-2"
})(["", ";", ";width:auto;flex:unset;"], sharedInputStyles, function (p) {
  return textFieldSizes[p.$size];
});
var InputShadow = styled.div.withConfig({
  displayName: "ComboBoxInputSelect__InputShadow",
  componentId: "vapor__sc-1ggv4n5-3"
})(["position:absolute;top:0;left:0;visibility:hidden;height:0;width:0;padding:0;overflow:scroll;white-space:pre;pointer-events:none;", ";"], function (p) {
  return textFieldSizes[p.$size];
});
var StyledValueContainer = styled.div.withConfig({
  displayName: "ComboBoxInputSelect__StyledValueContainer",
  componentId: "vapor__sc-1ggv4n5-4"
})(["padding-right:0;outline:none;display:flex;justify-content:space-between;"]);